export interface ThemeColors {
  background: string;
  surface: string;
  surfaceAlt: string;
  border: string;
  text: string;
  textMuted: string;
  textInverse: string;
  textInverseMuted: string;
  primary: string;
  onPrimary: string;
  dark: string;
  danger: string;
  onDanger: string;
  dangerSubtle: string;
  onDangerSubtle: string;
  success: string;
  onSuccess: string;
  successSubtle: string;
  onSuccessSubtle: string;
  warning: string;
  onWarning: string;
  warningSubtle: string;
  onWarningSubtle: string;
  info: string;
  onInfo: string;
  infoSubtle: string;
  onInfoSubtle: string;
  skeleton: string;
  chatBubbleIncoming: string;
  onChatBubbleIncoming: string;
  chatBubbleOutgoing: string;
  onChatBubbleOutgoing: string;
  mapAccuracyExcellent: string;
  mapAccuracyWarning: string;
  mapAccuracyCritical: string;
  mapCallerMarker: string;
  mapResidenceMarker: string;
  mapSourceMarker: string;
  mapTrack: string;
  mapTrackCasing: string;
}

export interface ThemeFontFamily {
  regular: string;
  medium: string;
  semibold: string;
  bold: string;
  heading: string;
}

export interface ThemeTokens {
  colors: ThemeColors;
  spacing: (steps: number) => number;
  radius: { sm: number; md: number; lg: number; pill: number };
  fontSize: { xs: number; sm: number; md: number; lg: number; xl: number };
  fontWeight: { regular: '400'; medium: '500'; semibold: '600'; bold: '700' };
  iconSize: { xs: number; sm: number; md: number; lg: number; xl: number };
  fontFamily?: ThemeFontFamily;
}

const SPACING_UNIT = 4;

const DEFAULT_PRIMARY = '#0d6efd';

export const defaultColors: ThemeColors = {
  background: '#f5f6f8',
  surface: '#ffffff',
  surfaceAlt: '#eef1f4',
  border: '#d5dbe1',
  text: '#1f2933',
  textMuted: '#6b7784',
  textInverse: '#ffffff',
  textInverseMuted: '#c7ccd1',
  primary: DEFAULT_PRIMARY,
  onPrimary: '#ffffff',
  dark: '#212529',
  danger: '#dc3545',
  onDanger: '#ffffff',
  dangerSubtle: '#f8d7da',
  onDangerSubtle: '#842029',
  success: '#198754',
  onSuccess: '#ffffff',
  successSubtle: '#d1e7dd',
  onSuccessSubtle: '#0f5132',
  warning: '#ffc107',
  onWarning: '#332701',
  warningSubtle: '#fff3cd',
  onWarningSubtle: '#664d03',
  info: '#0dcaf0',
  onInfo: '#032830',
  infoSubtle: '#cff4fc',
  onInfoSubtle: '#055160',
  skeleton: '#e3e7eb',
  chatBubbleIncoming: '#eef1f4',
  onChatBubbleIncoming: '#1f2933',
  chatBubbleOutgoing: DEFAULT_PRIMARY,
  onChatBubbleOutgoing: '#ffffff',
  mapAccuracyExcellent: '#009a38',
  mapAccuracyWarning: '#ffb500',
  mapAccuracyCritical: '#de000f',
  mapCallerMarker: '#dc3545',
  mapResidenceMarker: '#1f2933',
  mapSourceMarker: '#384951',
  mapTrack: '#999999',
  mapTrackCasing: '#444444',
};

export interface CreateThemeOptions {
  primary?: string;
  colors?: Partial<ThemeColors>;
  fontFamily?: ThemeFontFamily;
}

export function createTheme(options?: CreateThemeOptions): ThemeTokens {
  const branded: ThemeColors = {
    ...defaultColors,
    ...(options?.primary ? { primary: options.primary } : {}),
  };

  const colors: ThemeColors = {
    ...branded,
    chatBubbleOutgoing: branded.primary,
    onChatBubbleOutgoing: branded.onPrimary,
    ...options?.colors,
  };

  return {
    colors,
    spacing: (steps: number) => steps * SPACING_UNIT,
    radius: { sm: 4, md: 8, lg: 16, pill: 999 },
    fontSize: { xs: 12, sm: 14, md: 16, lg: 20, xl: 28 },
    fontWeight: { regular: '400', medium: '500', semibold: '600', bold: '700' },
    iconSize: { xs: 12, sm: 14, md: 18, lg: 24, xl: 32 },
    fontFamily: options?.fontFamily,
  };
}

export const defaultTheme: ThemeTokens = createTheme();
